import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AuditLogRow {
  id: string;
  actor_user_id: string;
  action: string;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown>;
  reason: string | null;
  created_at: string;
}

interface Profile {
  user_id: string;
  email: string | null;
  full_name: string | null;
}

const ACTION_TYPES = [
  '__all__',
  'billing_override',
  'school_suspend',
  'school_reactivate',
  'school_notes_updated',
  'user_password_reset',
  'user_email_update',
  'user_removed_from_school',
  'assessment_content_viewed',
  'assessment_deleted',
  'leads_exported',
] as const;

const TARGET_TYPES = ['__all__', 'school', 'user', 'assessment', 'leads'] as const;

export default function AdminAuditLog() {
  const navigate = useNavigate();
  const [actionFilter, setActionFilter] = useState('__all__');
  const [targetTypeFilter, setTargetTypeFilter] = useState('__all__');
  const [dateFrom, setDateFrom] = useState('');

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_admin_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as AuditLogRow[];
    },
  });

  const actorIds = useMemo(() => [...new Set(logs.map(l => l.actor_user_id))], [logs]);

  const { data: profiles = [] } = useQuery({
    queryKey: ['admin-audit-actors', actorIds],
    queryFn: async () => {
      if (actorIds.length === 0) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, email, full_name')
        .in('user_id', actorIds);
      if (error) throw error;
      return data as Profile[];
    },
    enabled: actorIds.length > 0,
  });

  const profileByUserId = useMemo(() => {
    const map = new Map<string, Profile>();
    for (const p of profiles) map.set(p.user_id, p);
    return map;
  }, [profiles]);

  const filtered = useMemo(() => {
    return logs.filter(l => {
      if (actionFilter !== '__all__' && l.action !== actionFilter) return false;
      if (targetTypeFilter !== '__all__' && l.target_type !== targetTypeFilter) return false;
      if (dateFrom) {
        const from = new Date(dateFrom).getTime();
        if (new Date(l.created_at).getTime() < from) return false;
      }
      return true;
    });
  }, [logs, actionFilter, targetTypeFilter, dateFrom]);

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const targetLink = (row: AuditLogRow) => {
    if (row.target_type === 'school') return `/admin/schools/${row.target_id}`;
    if (row.target_type === 'user') return `/admin/users/${row.target_id}`;
    return null;
  };

  const actionBadgeVariant = (action: string) => {
    if (action.includes('delete') || action.includes('suspend') || action.includes('remove')) return 'destructive' as const;
    if (action.includes('update') || action.includes('override') || action.includes('reset')) return 'secondary' as const;
    return 'outline' as const;
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Last 200 admin actions (read-only)</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_TYPES.map(a => (
                <SelectItem key={a} value={a}>{a === '__all__' ? 'All actions' : a.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={targetTypeFilter} onValueChange={setTargetTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All targets" />
            </SelectTrigger>
            <SelectContent>
              {TARGET_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t === '__all__' ? 'All targets' : t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="w-44"
            placeholder="From date"
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 font-medium">Timestamp</th>
                  <th className="text-left px-4 py-3 font-medium">Actor</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                  <th className="text-left px-4 py-3 font-medium">Target type</th>
                  <th className="text-left px-4 py-3 font-medium">Target</th>
                  <th className="text-left px-4 py-3 font-medium">Reason</th>
                  <th className="text-left px-4 py-3 font-medium">Metadata</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No entries found.</td>
                  </tr>
                ) : (
                  filtered.map(row => {
                    const actor = profileByUserId.get(row.actor_user_id);
                    const link = targetLink(row);
                    return (
                      <tr key={row.id} className="border-b last:border-0">
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                        <td className="px-4 py-3">
                          {actor ? (
                            <span title={actor.user_id}>{actor.email ?? actor.full_name ?? actor.user_id.slice(0, 8)}</span>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground">{row.actor_user_id.slice(0, 8)}…</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={actionBadgeVariant(row.action)} className="text-xs whitespace-nowrap">
                            {row.action.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">{row.target_type}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {link ? (
                            <button
                              className="font-mono text-xs text-primary hover:underline"
                              onClick={() => navigate(link)}
                            >
                              {row.target_id.slice(0, 8)}…
                            </button>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground">{row.target_id.slice(0, 8)}…</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground max-w-xs truncate" title={row.reason ?? undefined}>
                          {row.reason ?? '—'}
                        </td>
                        <td className="px-4 py-3">
                          {row.metadata && Object.keys(row.metadata).length > 0 ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-mono text-xs text-muted-foreground cursor-help underline decoration-dotted">
                                    {Object.keys(row.metadata).join(', ')}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-sm">
                                  <pre className="text-xs whitespace-pre-wrap break-all">
                                    {JSON.stringify(row.metadata, null, 2)}
                                  </pre>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</p>
      </div>
    </DashboardLayout>
  );
}
